import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Button, Card, CardBody, Input, Spinner, Table, Badge,
  Alert, FormGroup, Label, ButtonGroup
} from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EmojiPicker from "emoji-picker-react";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";
import NotificationConfigService from "../../services/notificationConfig";
import Breadcrumbs from "../../components/Breadcrumb";
import { CompanyId } from "../../constants";
import LocalstorageService from "../../helpers/localstorage-service";

const CHANNEL_LABELS = {
  0: { label: "ללא", color: "secondary" },
  1: { label: "SMS", color: "info" },
  2: { label: "WhatsApp", color: "success" },
  3: { label: "שניהם", color: "primary" },
};

const ROLE_LABELS = {
  1: "מנהל מערכת",
  2: "מנהל חברה",
  3: "סוכן",
  4: "עובד",
};

const SMS_SEGMENT_SIZE_DEFAULT = 134;
const WHATSAPP_MAX_CHARS = 4096;

// WhatsApp formatting toolbar
const FormatToolbar = ({ textareaRef, messageText, setMessageText }) => {
  const wrapSelection = useCallback((prefix, suffix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = messageText.substring(start, end);
    const before = messageText.substring(0, start);
    const after = messageText.substring(end);

    if (selected) {
      const newText = before + prefix + selected + suffix + after;
      setMessageText(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + prefix.length, end + prefix.length);
      }, 0);
    } else {
      const newText = before + prefix + suffix + after;
      setMessageText(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + prefix.length, start + prefix.length);
      }, 0);
    }
  }, [textareaRef, messageText, setMessageText]);

  return (
    <ButtonGroup size="sm" className="mb-1">
      <Button
        color="light"
        title="מודגש *bold*"
        onClick={() => wrapSelection("*", "*")}
        style={{ fontWeight: "bold", minWidth: 32 }}
      >
        B
      </Button>
      <Button
        color="light"
        title="נטוי _italic_"
        onClick={() => wrapSelection("_", "_")}
        style={{ fontStyle: "italic", minWidth: 32 }}
      >
        I
      </Button>
      <Button
        color="light"
        title="קו חוצה ~strikethrough~"
        onClick={() => wrapSelection("~", "~")}
        style={{ textDecoration: "line-through", minWidth: 32 }}
      >
        S
      </Button>
      <Button
        color="light"
        title="קוד ```monospace```"
        onClick={() => wrapSelection("```", "```")}
        style={{ fontFamily: "monospace", minWidth: 32 }}
      >
        {"</>"}
      </Button>
    </ButtonGroup>
  );
};

const SendMessage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const textareaRef = useRef(null);

  const [breadcrumbItems] = useState([
    { title: t("APPLICATION_TITLE"), link: "#" },
    { title: "ניהול כספומט", link: "#" },
    { title: "שליחת הודעות", link: "#" },
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [messageText, setMessageText] = useState("");
  const [channelOverride, setChannelOverride] = useState(null);
  const [smsSegmentSize, setSmsSegmentSize] = useState(SMS_SEGMENT_SIZE_DEFAULT);
  const [sendResult, setSendResult] = useState(null);
  const [optedInPhones, setOptedInPhones] = useState(new Set());
  const [userChannelOverrides, setUserChannelOverrides] = useState({});

  // Emoji picker
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiRef = useRef(null);

  // Scheduled message
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);

  // Scheduled messages list
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [editingScheduledId, setEditingScheduledId] = useState(null);

  useEffect(() => {
    loadUsers();
    loadScheduledMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close emoji picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      const [usersRes, optInRes] = await Promise.all([
        NotificationConfigService.getUserNotificationPreferences(companyId),
        NotificationConfigService.getWhatsAppOptInStatus(companyId),
      ]);

      if (usersRes.data?.status === 200 && usersRes.data.data) {
        setUsers(usersRes.data.data.filter(u => u.phoneNumber));
      }
      if (optInRes.data?.status === 200 && optInRes.data.data) {
        setOptedInPhones(new Set(optInRes.data.data.phones || []));
      }

      try {
        const paramsResponse = NotificationConfigService.getCompanyParameters
          ? await NotificationConfigService.getCompanyParameters(companyId)
          : null;
        if (paramsResponse?.data?.data?.smsSegmentSize) {
          setSmsSegmentSize(paramsResponse.data.data.smsSegmentSize);
        }
      } catch { /* use default */ }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("שגיאה בטעינת משתמשים");
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const loadScheduledMessages = async () => {
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      const res = await NotificationConfigService.getScheduledMessages(companyId);
      if (res.data?.status === 200 && Array.isArray(res.data.data)) {
        setScheduledMessages(res.data.data);
      } else {
        setScheduledMessages([]);
      }
    } catch {
      // Table may not exist yet — silently ignore
      setScheduledMessages([]);
    }
  };

  const hasOptIn = (user) => optedInPhones.has(user.phoneNumber);

  const selectedUsers = useMemo(
    () => users.filter(u => selectedUserIds.has(u.userId)),
    [users, selectedUserIds]
  );

  const isSingleUser = selectedUsers.length === 1;
  const isMultipleUsers = selectedUsers.length > 1;

  const effectiveChannel = useMemo(() => {
    if (isSingleUser) {
      return channelOverride ?? selectedUsers[0].deliveryChannel;
    }
    return null;
  }, [isSingleUser, channelOverride, selectedUsers]);

  const isWhatsAppMode = isSingleUser && (effectiveChannel === 2 || effectiveChannel === 3);
  const singleUserHasOptIn = isSingleUser && selectedUsers[0] && hasOptIn(selectedUsers[0]);

  const charCount = messageText.length;
  const maxChars = isWhatsAppMode && singleUserHasOptIn ? WHATSAPP_MAX_CHARS : smsSegmentSize * 10;
  const segmentCount = charCount > 0 ? Math.ceil(charCount / smsSegmentSize) : 0;

  const selectedWithoutOptIn = useMemo(
    () => selectedUsers.filter(u => !hasOptIn(u)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedUsers, optedInPhones]
  );

  // Handlers
  const toggleUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
    setChannelOverride(null);
    setSendResult(null);
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map(u => u.userId)));
    }
    setChannelOverride(null);
    setSendResult(null);
  };

  const setUserChannelOverride = (userId, channel) => {
    setUserChannelOverrides(prev => ({ ...prev, [userId]: channel }));
  };

  const onEmojiClick = (emojiData) => {
    const textarea = textareaRef.current;
    const cursor = textarea ? textarea.selectionStart : messageText.length;
    const before = messageText.substring(0, cursor);
    const after = messageText.substring(cursor);
    setMessageText(before + emojiData.emoji + after);
    setShowEmojiPicker(false);
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newPos = cursor + emojiData.emoji.length;
        textarea.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleSend = async () => {
    if (selectedUsers.length === 0) {
      toast.warning("נא לבחור לפחות משתמש אחד");
      return;
    }
    if (!messageText.trim()) {
      toast.warning("נא להזין הודעה");
      return;
    }
    if (isScheduled && !scheduledDate) {
      toast.warning("נא לבחור תאריך ושעה לשליחה");
      return;
    }
    if (isScheduled && scheduledDate <= new Date()) {
      toast.warning("מועד השליחה חייב להיות בעתיד");
      return;
    }

    // Validate channel selection
    if (isSingleUser && selectedUsers[0].deliveryChannel === 0 && !channelOverride && !userChannelOverrides[selectedUsers[0].userId]) {
      toast.warning("נא לבחור ערוץ שליחה (SMS / WhatsApp)");
      return;
    }
    const noChannelUsers = selectedUsers.filter(u => u.deliveryChannel === 0 && !userChannelOverrides[u.userId]);
    if (noChannelUsers.length > 0 && !isSingleUser) {
      toast.warning(`${noChannelUsers.length} משתמשים ללא ערוץ שליחה — נא לבחור ערוץ עבורם`);
      return;
    }

    setIsSending(true);
    setSendResult(null);
    dispatch(setLoadingOverlay(true));

    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      let overrideChannel = null;
      if (isSingleUser) {
        // Always send the effective channel — user's default or explicit override
        overrideChannel = channelOverride ?? selectedUsers[0].deliveryChannel;
        if (overrideChannel === 0) overrideChannel = null; // 0 = no channel set
      }

      const payload = {
        companyId: parseInt(companyId),
        userIds: Array.from(selectedUserIds),
        message: messageText.trim(),
        deliveryChannelOverride: overrideChannel,
        userChannelOverrides: isMultipleUsers ? userChannelOverrides : undefined,
      };

      if (isScheduled && scheduledDate) {
        // Send local time string (server stores Israel time)
        const pad = (n) => String(n).padStart(2, '0');
        const localStr = `${scheduledDate.getFullYear()}-${pad(scheduledDate.getMonth()+1)}-${pad(scheduledDate.getDate())}T${pad(scheduledDate.getHours())}:${pad(scheduledDate.getMinutes())}:00`;
        payload.scheduledAt = localStr;
      }

      if (editingScheduledId) {
        payload.scheduledMessageId = editingScheduledId;
      }

      console.log("SendMessage PAYLOAD:", JSON.stringify(payload));
      const response = await NotificationConfigService.sendMessageToUsers(payload);
      console.log("SendMessage RESPONSE:", JSON.stringify(response.data));

      if (response.data?.status === 200) {
        const result = response.data.data;
        setSendResult(result);
        if (isScheduled) {
          toast.success(`הודעה תזומנה לשליחה ב-${scheduledDate.toLocaleString("he-IL")}`);
        } else if (result.failedCount === 0) {
          toast.success(`הודעה נשלחה בהצלחה ל-${result.sentCount} משתמשים`);
        } else {
          toast.warning(`נשלחו ${result.sentCount}, נכשלו ${result.failedCount}`);
        }
        setMessageText("");
        setSelectedUserIds(new Set());
        setUserChannelOverrides({});
        setIsScheduled(false);
        setScheduledDate(null);
        setEditingScheduledId(null);
        loadScheduledMessages();
      } else {
        toast.error(response.data?.message || "שגיאה בשליחה");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("שגיאה בשליחת הודעה");
    } finally {
      setIsSending(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const editScheduledMessage = (msg) => {
    setEditingScheduledId(msg.id);
    setMessageText(msg.message);
    setSelectedUserIds(new Set(msg.userIds));
    setIsScheduled(true);
    setScheduledDate(new Date(msg.scheduledAt));
    if (msg.deliveryChannelOverride) {
      setChannelOverride(msg.deliveryChannelOverride);
    }
    toast.info("עורך הודעה מתוזמנת — ניתן לשנות ולשמור");
  };

  const cancelScheduledMessage = async (id) => {
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      await NotificationConfigService.cancelScheduledMessage(companyId, id);
      toast.success("הודעה מתוזמנת בוטלה");
      loadScheduledMessages();
    } catch {
      toast.error("שגיאה בביטול");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content" style={{ paddingBottom: "10px" }}>
        <Breadcrumbs title="שליחת הודעות" breadcrumbItems={breadcrumbItems} />

        <Card className="mb-2">
          <CardBody className="py-3">
            <h5 className="card-title mb-3">
              <i className="ri-send-plane-line me-2"></i>
              שליחת הודעה למשתמשים
            </h5>

            {isLoading ? (
              <div className="text-center py-3"><Spinner color="primary" /></div>
            ) : users.length === 0 ? (
              <div className="text-center py-3 text-muted">לא נמצאו משתמשים עם מספר טלפון</div>
            ) : (
              <>
                {/* User table — compact */}
                <div style={{ maxHeight: "220px", overflowY: "auto" }} className="mb-2 border rounded">
                  <Table striped bordered hover responsive size="sm" className="mb-0" style={{ fontSize: "0.85rem" }}>
                    <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: "36px" }} className="text-center p-1">
                          <Input type="checkbox" checked={selectedUserIds.size === users.length && users.length > 0} onChange={toggleSelectAll} style={{ cursor: "pointer" }} />
                        </th>
                        <th className="p-1">שם</th>
                        <th className="p-1">תפקיד</th>
                        <th className="p-1">טלפון</th>
                        <th className="text-center p-1" style={{ width: "100px" }}>ערוץ</th>
                        <th className="text-center p-1" style={{ width: "50px" }}>WA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const userOptIn = hasOptIn(user);
                        const isSelected = selectedUserIds.has(user.userId);
                        const isNoChannel = user.deliveryChannel === 0;
                        return (
                          <tr key={user.userId} onClick={() => toggleUser(user.userId)} style={{ cursor: "pointer" }} className={isSelected ? "table-primary" : ""}>
                            <td className="text-center p-1" onClick={(e) => e.stopPropagation()}>
                              <Input type="checkbox" checked={isSelected} onChange={() => toggleUser(user.userId)} style={{ cursor: "pointer" }} />
                            </td>
                            <td className="p-1">{user.firstName} {user.lastName}</td>
                            <td className="p-1">{ROLE_LABELS[user.roleId] || user.roleName}</td>
                            <td dir="ltr" className="text-end p-1">{user.phoneNumber}</td>
                            <td className="text-center p-1" onClick={(e) => e.stopPropagation()}>
                              {isSelected && isNoChannel ? (
                                <Input type="select" bsSize="sm" value={userChannelOverrides[user.userId] || ""} onChange={(e) => { e.stopPropagation(); setUserChannelOverride(user.userId, parseInt(e.target.value) || null); }} style={{ width: "90px", display: "inline-block", fontSize: "0.8rem" }}>
                                  <option value="">בחר...</option>
                                  <option value="1">SMS</option>
                                  <option value="2">WhatsApp</option>
                                </Input>
                              ) : (
                                <Badge color={CHANNEL_LABELS[user.deliveryChannel]?.color || "secondary"} style={{ fontSize: "0.75rem" }}>
                                  {CHANNEL_LABELS[user.deliveryChannel]?.label || "ללא"}
                                </Badge>
                              )}
                            </td>
                            <td className="text-center p-1">
                              {userOptIn
                                ? <span style={{ color: "#28a745" }} title="אישור WhatsApp פעיל">&#x2705;</span>
                                : <span style={{ color: "#999", fontSize: "0.75rem" }}>—</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Status bar */}
                <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: "0.85rem" }}>
                  <span className="text-muted">
                    נבחרו <strong>{selectedUsers.length}</strong> / {users.length}
                    {selectedWithoutOptIn.length > 0 && selectedUsers.length > 0 && (
                      <span className="text-warning ms-2">
                        <i className="ri-alert-line"></i> {selectedWithoutOptIn.length} ללא WA — SMS
                      </span>
                    )}
                  </span>

                  {/* Channel override for single user */}
                  {isSingleUser && (
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">ערוץ:</small>
                      <ButtonGroup size="sm">
                        <Button color={effectiveChannel === 1 ? "info" : "outline-info"} onClick={() => setChannelOverride(1)} style={{ fontSize: "0.75rem", padding: "2px 8px" }}>SMS</Button>
                        <Button color={effectiveChannel === 2 ? "success" : "outline-success"} onClick={() => setChannelOverride(2)} disabled={!singleUserHasOptIn} style={{ fontSize: "0.75rem", padding: "2px 8px" }}>
                          WhatsApp {!singleUserHasOptIn && "🔒"}
                        </Button>
                        {selectedUsers[0]?.deliveryChannel > 0 && (
                          <Button color={!channelOverride ? "light" : "outline-secondary"} onClick={() => setChannelOverride(null)} style={{ fontSize: "0.75rem", padding: "2px 8px" }}>
                            ברירת מחדל
                          </Button>
                        )}
                      </ButtonGroup>
                    </div>
                  )}
                </div>

                {/* Message compose area */}
                <div className="border rounded p-2 mb-2" style={{ backgroundColor: "#fafbfc" }}>
                  {/* Toolbar row */}
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <div className="d-flex align-items-center gap-2">
                      {(isWhatsAppMode && singleUserHasOptIn) && (
                        <FormatToolbar textareaRef={textareaRef} messageText={messageText} setMessageText={setMessageText} />
                      )}

                      {/* Emoji button */}
                      <div style={{ position: "relative", display: "inline-block" }} ref={emojiRef}>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          title="הוסף אימוג׳י"
                          style={{ fontSize: "1.1rem", padding: "2px 8px", lineHeight: 1 }}
                        >
                          😊
                        </Button>
                        {showEmojiPicker && (
                          <div style={{
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 9999,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                            borderRadius: "12px",
                            overflow: "hidden"
                          }}>
                            <EmojiPicker
                              onEmojiClick={onEmojiClick}
                              width={350}
                              height={420}
                              searchPlaceholder="חפש אימוג׳י..."
                              previewConfig={{ showPreview: false }}
                              skinTonesDisabled
                              lazyLoadEmojis
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Char counter */}
                    <small className={charCount > maxChars ? "text-danger fw-bold" : "text-muted"}>
                      {isWhatsAppMode && singleUserHasOptIn ? (
                        <>{charCount} / {WHATSAPP_MAX_CHARS} <i className="ri-whatsapp-line text-success"></i></>
                      ) : (
                        <>{charCount} תווים | {segmentCount} מקטעי SMS</>
                      )}
                    </small>
                  </div>

                  {/* Textarea */}
                  <Input
                    type="textarea"
                    innerRef={textareaRef}
                    rows={3}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder={
                      isWhatsAppMode && singleUserHasOptIn
                        ? "כתוב הודעה... ניתן להשתמש ב-*מודגש* _נטוי_ ~קו חוצה~"
                        : "כתוב הודעה..."
                    }
                    dir="rtl"
                    disabled={isSending}
                    style={{ resize: "none", fontSize: "0.9rem" }}
                  />
                </div>

                {/* Bottom row: schedule toggle + send button */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  {/* Schedule toggle */}
                  <div className="d-flex align-items-center gap-2">
                    <FormGroup check className="mb-0">
                      <Input
                        type="checkbox"
                        id="scheduleToggle"
                        checked={isScheduled}
                        onChange={(e) => {
                          setIsScheduled(e.target.checked);
                          if (!e.target.checked) { setScheduledDate(null); setEditingScheduledId(null); }
                        }}
                      />
                      <Label check for="scheduleToggle" style={{ fontSize: "0.85rem", cursor: "pointer" }}>
                        <i className="ri-time-line me-1"></i>
                        שליחה דחויה
                      </Label>
                    </FormGroup>

                    {isScheduled && (
                      <DatePicker
                        selected={scheduledDate}
                        onChange={(date) => setScheduledDate(date)}
                        showTimeSelect
                        timeIntervals={1}
                        timeFormat="HH:mm"
                        dateFormat="dd/MM/yyyy HH:mm"
                        minDate={new Date()}
                        placeholderText="בחר תאריך ושעה"
                        className="form-control form-control-sm"
                        style={{ width: "180px" }}
                      />
                    )}
                  </div>

                  {/* Send buttons */}
                  <div className="d-flex gap-2">
                    {/* Immediate send — always visible */}
                    {!isScheduled && (
                      <Button
                        color="primary"
                        onClick={handleSend}
                        disabled={isSending || selectedUsers.length === 0 || !messageText.trim()}
                        size="sm"
                        className="px-4"
                      >
                        {isSending ? (
                          <><Spinner size="sm" className="me-1" /> שולח...</>
                        ) : (
                          <><i className="ri-send-plane-fill me-1"></i> שלח ({selectedUsers.length})</>
                        )}
                      </Button>
                    )}
                    {/* Schedule button — only when scheduled toggle is on */}
                    {isScheduled && (
                      <Button
                        color="warning"
                        onClick={handleSend}
                        disabled={isSending || selectedUsers.length === 0 || !messageText.trim() || !scheduledDate}
                        size="sm"
                        className="px-4"
                      >
                        {isSending ? (
                          <><Spinner size="sm" className="me-1" /> שומר...</>
                        ) : editingScheduledId ? (
                          <><i className="ri-edit-line me-1"></i> עדכן</>
                        ) : (
                          <><i className="ri-calendar-check-line me-1"></i> תזמן ({selectedUsers.length})</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Send results */}
                {sendResult && (
                  <Alert color={sendResult.failedCount === 0 ? "success" : "warning"} className="mt-2 mb-0 py-2" style={{ fontSize: "0.85rem" }}>
                    <strong>תוצאה:</strong> נשלחו {sendResult.sentCount}, נכשלו {sendResult.failedCount}
                    {sendResult.results && sendResult.results.length > 0 && (
                      <span className="ms-2">
                        | ch={sendResult.results[0].channel} optIn={String(sendResult.results[0].hasOptIn)} sms={sendResult.results[0].smsStatus} wa={sendResult.results[0].waStatus}
                      </span>
                    )}
                  </Alert>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* Scheduled messages list */}
        {scheduledMessages.length > 0 && (
          <Card>
            <CardBody className="py-3">
              <h6 className="mb-2"><i className="ri-calendar-todo-line me-1"></i> הודעות מתוזמנות</h6>
              <Table size="sm" bordered className="mb-0" style={{ fontSize: "0.82rem" }}>
                <thead className="table-light">
                  <tr>
                    <th className="p-1">מועד שליחה</th>
                    <th className="p-1">נמענים</th>
                    <th className="p-1">הודעה</th>
                    <th className="p-1 text-center" style={{ width: "120px" }}>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledMessages.map((msg) => (
                    <tr key={msg.id}>
                      <td className="p-1">{new Date(msg.scheduledAt).toLocaleString("he-IL")}</td>
                      <td className="p-1">{msg.recipientCount} משתמשים</td>
                      <td className="p-1" style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{msg.message}</td>
                      <td className="p-1 text-center">
                        <Button color="info" size="sm" className="me-1 px-2 py-0" onClick={() => editScheduledMessage(msg)} title="ערוך">
                          <i className="ri-edit-line"></i>
                        </Button>
                        <Button color="danger" size="sm" className="px-2 py-0" onClick={() => cancelScheduledMessage(msg.id)} title="בטל">
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        )}
      </div>
    </React.Fragment>
  );
};

export default SendMessage;
